// Lib
import React, { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import classNames from 'classnames';

// Components
import ToggleInput from '../../../../components/form/fields/ToggleInput';
import SettingsRow from '../../../../user/account/accountModal/components/SettingsRow';
import StyledButton from '../../../../components/buttons/StyledButton';
import PopupPanelAsideSection from '../../../../components/popupPanel/types/asideSection/PopupPanelAsideSection';

// Utils
import useLocalStorage from '../../../../utils/services/localStorage/useLocalStorage';
import usePrevious from '../../../../utils/react/usePrevious';
import useDebounce from '../../../../utils/react/useDebouncedValue';

// Selectors
import { isPresentationModeEnabledSelector } from '../../../presentation/presentationSelector';

// Actions
import {
    endPresentationMode,
    startPresentationMode,
    updatePresentationModeSettings,
} from '../../../presentation/presentationActions';

// Styles
import './PresentationModePopupPanelSection.scss';
import {
    PRESENTATION_MODE_DISABLE_EDITING_KEY,
    PRESENTATION_MODE_FOCUS_MODE_ALLOWED_KEY,
    PRESENTATION_MODE_HIDE_COMMENTS_KEY,
} from '../../../presentation/presentationConstants';

type PresentationModeSettings = { hideComments: boolean; disableEditing: boolean; isFocusModeAllowed: boolean };

const mapStateToProps = createStructuredSelector({
    isPresentationMode: isPresentationModeEnabledSelector,
});

const mapDispatchToProps = (dispatch: Function) => ({
    dispatchStartPresentationMode: (args: PresentationModeSettings) => {
        return dispatch(startPresentationMode(args));
    },
    dispatchEndPresentationMode: () => dispatch(endPresentationMode()),
    dispatchUpdatePresentationModeSettings: (args: PresentationModeSettings) =>
        dispatch(updatePresentationModeSettings(args)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const PresentationModePopupPanelSection = ({
    isPresentationMode,
    dispatchStartPresentationMode,
    dispatchEndPresentationMode,
    dispatchUpdatePresentationModeSettings,
}: PropsFromRedux): React.ReactElement => {
    const [isHideCommentsEnabled, setIsHideCommentsEnabled] = useLocalStorage(
        PRESENTATION_MODE_HIDE_COMMENTS_KEY,
        false,
    );
    const [isEditingDisabled, setIsEditingDisabled] = useLocalStorage(PRESENTATION_MODE_DISABLE_EDITING_KEY, false);
    const [isFocusModeAllowed, setIsFocusModeAllowed] = useLocalStorage(PRESENTATION_MODE_FOCUS_MODE_ALLOWED_KEY, true);

    const showToggles = useDebounce(isPresentationMode, 400);
    const previousShowTogglesValue = usePrevious(showToggles);

    useEffect(() => {
        const handler = setTimeout(() => {
            if (isPresentationMode) {
                dispatchUpdatePresentationModeSettings({
                    hideComments: isHideCommentsEnabled,
                    disableEditing: isEditingDisabled,
                    isFocusModeAllowed,
                });
            }
        }, 100);

        return () => {
            clearTimeout(handler);
        };
    }, [isHideCommentsEnabled, isEditingDisabled, isFocusModeAllowed]);

    const onClick = () => {
        isPresentationMode
            ? dispatchEndPresentationMode()
            : dispatchStartPresentationMode({
                  hideComments: isHideCommentsEnabled,
                  disableEditing: isEditingDisabled,
                  isFocusModeAllowed,
              });
    };

    return (
        <PopupPanelAsideSection className="PresentationModePopupPanelSection">
            <h3>Presentation mode</h3>
            <p className="explanation">Go full-screen & hide toolbar, dot grid etc.</p>
            <StyledButton className="present-button action small" onClick={onClick}>
                {isPresentationMode ? (
                    <>
                        <span>Stop presenting</span>
                        <span className="sub-text"> (Esc)</span>
                    </>
                ) : (
                    'Present'
                )}
            </StyledButton>

            <div
                className={classNames('toggles-section', {
                    presenting: showToggles,
                    'presenting-exiting': previousShowTogglesValue && !showToggles,
                })}
            >
                <SettingsRow className="toggle-row" label="Focus on selected cards">
                    <ToggleInput
                        active={isFocusModeAllowed}
                        onChange={(event) => setIsFocusModeAllowed(event.target.checked)}
                    />
                </SettingsRow>
                <SettingsRow className="toggle-row" disabled={false} label="Hide comments">
                    <ToggleInput
                        active={isHideCommentsEnabled}
                        onChange={(event) => setIsHideCommentsEnabled(event.target.checked)}
                    />
                </SettingsRow>
                <SettingsRow className="toggle-row" label="Prevent editing">
                    <ToggleInput
                        active={isEditingDisabled}
                        onChange={(event) => setIsEditingDisabled(event.target.checked)}
                    />
                </SettingsRow>
            </div>
        </PopupPanelAsideSection>
    );
};

export default connector(PresentationModePopupPanelSection);
